import { useEffect, useRef, useState } from 'react';
import { Button, Input, TextArea } from '../../design-system/primitives/index.js';

/**
 * Ways to bring in recipes someone already has.
 * Desktop: four equal choices, then the open form.
 * Mobile: one list of the same four actions.
 */
const ACTIONS = [
  {
    id: 'link',
    icon: '🔗',
    title: 'Paste a link',
    desc: 'NYT Cooking, Smitten Kitchen, food blogs',
  },
  {
    id: 'text',
    icon: '📋',
    title: 'Paste recipe text',
    desc: 'Notes, captions, or blog copy',
  },
  {
    id: 'photo',
    icon: '📷',
    title: 'Upload a photo',
    desc: 'Cookbook page or screenshot',
  },
  {
    id: 'account',
    icon: '📂',
    title: 'Connect an account',
    desc: 'NYT Cooking, Paprika, or Drive',
  },
];

export default function OwnRecipesSection({
  variant = 'desktop',
  headingLevel = 2,
  urlImportValue,
  onUrlImportChange,
  onUrlImport,
  urlImportLoading,
  urlImportError,
  pasteImportValue,
  onPasteImportChange,
  onPasteImport,
  pasteImportLoading,
  pasteImportError,
  onPhotoImport,
  onConnectedAccount,
  onboarding = false,
}) {
  const [openAction, setOpenAction] = useState(() => {
    if (urlImportValue?.trim() || urlImportError) return 'link';
    if (pasteImportValue?.trim() || pasteImportError) return 'text';
    return null;
  });
  const urlInputRef = useRef(null);
  const pasteInputRef = useRef(null);
  const skipInitialErrorOpen = useRef(true);
  const urlExpanded = openAction === 'link';
  const pasteExpanded = openAction === 'text';

  useEffect(() => {
    if (skipInitialErrorOpen.current) return;
    if (urlImportError) setOpenAction('link');
  }, [urlImportError]);

  useEffect(() => {
    if (skipInitialErrorOpen.current) return;
    if (pasteImportError) setOpenAction('text');
  }, [pasteImportError]);

  useEffect(() => {
    skipInitialErrorOpen.current = false;
  }, []);

  useEffect(() => {
    if (urlExpanded) urlInputRef.current?.focus();
  }, [urlExpanded]);

  useEffect(() => {
    if (pasteExpanded) pasteInputRef.current?.focus();
  }, [pasteExpanded]);

  const expanded = {
    link: urlExpanded,
    text: pasteExpanded,
    photo: false,
    account: false,
  };

  function activate(id) {
    if (id === 'link' || id === 'text') {
      setOpenAction((current) => (current === id ? null : id));
      return;
    }
    setOpenAction(null);
    if (id === 'photo') onPhotoImport?.();
    else if (id === 'account') onConnectedAccount?.();
  }

  const showPanel = variant === 'desktop' && (urlExpanded || pasteExpanded);
  const headingId = variant === 'mobile' ? 'rp-own-recipes-title-mobile' : 'rp-own-recipes-title';
  const TitleTag = headingLevel === 1 ? 'h1' : 'h2';
  const actions = onboarding ? ACTIONS.filter((action) => action.id !== 'account') : ACTIONS;
  const heading = onboarding ? 'Bring a recipe you already have.' : 'From wherever you keep them';
  const headingClass = onboarding
    ? 'onboarding-recipes__section'
    : (variant === 'mobile'
      ? 'rp-mobile-add-heading rp-mobile-add-heading-tight rp-own-recipes-heading'
      : 'rp-section-title');

  return (
    <section
      className={`rp-own-recipes${variant === 'mobile' ? ' rp-own-recipes--mobile' : ''}${onboarding ? ' rp-own-recipes--onboarding' : ''}`}
      aria-labelledby={headingId}
    >
      {variant === 'mobile' || onboarding ? (
        <TitleTag id={headingId} className={headingClass}>
          {heading}
        </TitleTag>
      ) : (
        <div className="rp-add-section-header">
          <TitleTag id={headingId} className={headingClass}>{heading}</TitleTag>
        </div>
      )}

      {variant === 'mobile' ? (
        <div className="rp-own-list">
          {actions.map((action) => (
            <OwnRecipeRow
              key={action.id}
              action={action}
              expanded={expanded[action.id]}
              onActivate={() => activate(action.id)}
            >
              {action.id === 'link' && urlExpanded && (
                <UrlImportFields
                  variant="mobile"
                  inputId="rp-own-url-input-mobile"
                  bodyId="rp-own-url-body-mobile"
                  inputRef={urlInputRef}
                  value={urlImportValue}
                  onChange={onUrlImportChange}
                  onImport={onUrlImport}
                  loading={urlImportLoading}
                  error={urlImportError}
                />
              )}
              {action.id === 'text' && pasteExpanded && (
                <PasteImportFields
                  variant="mobile"
                  inputId="rp-own-paste-input-mobile"
                  bodyId="rp-own-paste-body-mobile"
                  inputRef={pasteInputRef}
                  value={pasteImportValue}
                  onChange={onPasteImportChange}
                  onImport={onPasteImport}
                  loading={pasteImportLoading}
                  error={pasteImportError}
                />
              )}
            </OwnRecipeRow>
          ))}
        </div>
      ) : (
        <>
          <ul className={`rp-own-choices${onboarding ? ' rp-own-choices--three' : ''}`}>
            {actions.map((action) => {
              const isExpandable = action.id === 'link' || action.id === 'text';
              const isOpen = expanded[action.id];
              return (
                <li key={action.id}>
                  <h3 className="rp-own-choice-heading" aria-labelledby={`rp-own-choice-title-${action.id}`}>
                    <button
                      type="button"
                      className={`rp-own-choice${isOpen ? ' is-selected' : ''}`}
                      aria-expanded={isExpandable ? isOpen : undefined}
                      aria-controls={isOpen ? (action.id === 'link' ? 'rp-own-url-body' : 'rp-own-paste-body') : undefined}
                      onClick={() => activate(action.id)}
                    >
                      <span className="rp-own-choice-icon" aria-hidden="true">{action.icon}</span>
                      <span id={`rp-own-choice-title-${action.id}`} className="rp-own-choice-title">{action.title}</span>
                      <span className="rp-own-choice-desc">{action.desc}</span>
                    </button>
                  </h3>
                </li>
              );
            })}
          </ul>
          {showPanel && (
            <div className="rp-own-panel">
              {urlExpanded && (
                <UrlImportFields
                  variant="desktop"
                  inputId="rp-own-url-input"
                  bodyId="rp-own-url-body"
                  inputRef={urlInputRef}
                  value={urlImportValue}
                  onChange={onUrlImportChange}
                  onImport={onUrlImport}
                  loading={urlImportLoading}
                  error={urlImportError}
                />
              )}
              {pasteExpanded && (
                <PasteImportFields
                  variant="desktop"
                  inputId="rp-own-paste-input"
                  bodyId="rp-own-paste-body"
                  inputRef={pasteInputRef}
                  value={pasteImportValue}
                  onChange={onPasteImportChange}
                  onImport={onPasteImport}
                  loading={pasteImportLoading}
                  error={pasteImportError}
                />
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function OwnRecipeRow({ action, expanded, onActivate, children }) {
  const expandable = action.id === 'link' || action.id === 'text';
  const bodyId = action.id === 'link' ? 'rp-own-url-body-mobile' : 'rp-own-paste-body-mobile';

  return (
    <div className={`rp-own-row${expanded ? ' is-open' : ''}`}>
      <h3 className="rp-own-row-heading" aria-labelledby={`rp-own-row-title-${action.id}`}>
        <button
          type="button"
          className="rp-own-row-toggle"
          aria-expanded={expandable ? expanded : undefined}
          aria-controls={expanded ? bodyId : undefined}
          onClick={onActivate}
        >
          <span className="rp-own-row-icon" aria-hidden="true">{action.icon}</span>
          <span className="rp-own-row-copy">
            <span id={`rp-own-row-title-${action.id}`} className="rp-own-row-title">{action.title}</span>
            <span className="rp-own-row-desc">{action.desc}</span>
          </span>
          <span className={`rp-own-row-chevron${expanded ? ' open' : ''}`} aria-hidden="true">›</span>
        </button>
      </h3>
      {children}
    </div>
  );
}

function UrlImportFields({
  variant,
  inputId,
  bodyId,
  inputRef,
  value,
  onChange,
  onImport,
  loading,
  error,
}) {
  const mobile = variant === 'mobile';
  return (
    <div id={bodyId} className="rp-own-row-body">
      <label htmlFor={inputId} className="sr-only">Recipe URL</label>
      {mobile ? (
        <Input
          ref={inputRef}
          id={inputId}
          type="url"
          className="rp-url-import-input"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="https://cooking.nytimes.com/…"
          disabled={loading}
          invalid={Boolean(error)}
          onKeyDown={(e) => { if (e.key === 'Enter') onImport?.(); }}
        />
      ) : (
        <div className="rp-url-row">
          <Input
            ref={inputRef}
            id={inputId}
            type="url"
            className="rp-url-import-input-inline"
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="https://cooking.nytimes.com/recipes/…"
            autoComplete="off"
            disabled={loading}
            invalid={Boolean(error)}
            onKeyDown={(e) => { if (e.key === 'Enter') onImport?.(); }}
          />
          <Button
            type="button"
            variant="primary"
            className="rp-url-import-btn-inline"
            onClick={onImport}
            disabled={loading || !value?.trim()}
          >
            {loading ? 'Importing…' : 'Import →'}
          </Button>
        </div>
      )}
      {mobile && (
        <Button
          type="button"
          variant="primary"
          className="rp-url-import-btn"
          onClick={onImport}
          disabled={loading || !value?.trim()}
        >
          {loading ? 'Importing…' : 'Import →'}
        </Button>
      )}
      {error && (
        <div className={`rp-url-import-error${mobile ? '' : ' rp-url-import-error--tight'}`} role="alert">{error}</div>
      )}
    </div>
  );
}

function PasteImportFields({
  variant,
  inputId,
  bodyId,
  inputRef,
  value,
  onChange,
  onImport,
  loading,
  error,
}) {
  const mobile = variant === 'mobile';
  return (
    <div id={bodyId} className="rp-own-row-body">
      <label htmlFor={inputId} className="sr-only">Recipe text</label>
      <TextArea
        ref={inputRef}
        id={inputId}
        className="rp-paste-textarea"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={mobile ? 'Paste a full recipe here…' : 'Paste a full recipe here (title, ingredients, directions)…'}
        rows={mobile ? 5 : 6}
        disabled={loading}
        invalid={Boolean(error)}
      />
      <div className={mobile ? undefined : 'rp-paste-actions'}>
        <Button
          type="button"
          variant="primary"
          className={mobile ? 'rp-url-import-btn' : 'rp-url-import-btn-inline'}
          onClick={onImport}
          disabled={loading || !(value || '').trim()}
        >
          {loading ? 'Importing…' : 'Import text →'}
        </Button>
      </div>
      {error && (
        <div className={`rp-url-import-error${mobile ? '' : ' rp-url-import-error--tight'}`} role="alert">{error}</div>
      )}
    </div>
  );
}
